import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { StatusDot } from '@/components/StatusDot';
import { RatioBar } from '@/components/RatioBar';
import { EmptyState } from '@/components/EmptyState';
import { AlertCircle, Plus } from 'lucide-react';
import { me } from '@/lib/auth';
import { formatDate } from '@/lib/locale';
import { apiGet } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';
import { DomainAddModal } from './DomainAddModal';

type DomainRow = {
  id: string;
  fqdn: string;
  organizational_domain?: string;
  desired_policy?: 'none' | 'quarantine' | 'reject';
  current_policy?: 'none' | 'quarantine' | 'reject';
  monitored_since?: string;
  status?: 'good' | 'warn' | 'fail' | 'onboarding';
  // 0..1 fractions over the last 30 days (null when the domain has no report
  // data yet). RatioBar wants 0..100, so multiply at the render site.
  messages_30d?: number | null;
  pass_30d?: number | null;
  spf_pass_30d?: number | null;
  dkim_pass_30d?: number | null;
  alerts_count?: number;
};

type SortKey =
  | 'fqdn'
  | 'pass_30d'
  | 'spf_pass_30d'
  | 'dkim_pass_30d'
  | 'alerts_count'
  | 'monitored_since';

// Status dot derived from the DMARC pass-ratio (the headline SLA). No report
// data yet → neutral "onboarding" rather than a misleading green.
function statusFromRates(pass?: number | null): 'good' | 'warn' | 'fail' | 'onboarding' {
  if (pass == null) return 'onboarding';
  if (pass >= 0.95) return 'good';
  if (pass >= 0.9) return 'warn';
  return 'fail';
}

// Backend rates are 0..1 fractions; RatioBar wants 0..100. Null passes through
// as "no data" (RatioBar renders "—").
function pctOf(fraction?: number | null): number | null {
  return fraction == null ? null : fraction * 100;
}

export function DomainList({ slug }: { slug: string }) {
  const domainsQ = useQuery({
    queryKey: ['domains', slug],
    queryFn: () => apiGet<DomainRow[]>(`/tenants/${slug}/domains`, { tenant: slug }),
  });
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: () => me() });
  const canCreate = meQ.data?.platform_role === 'admin' || meQ.data?.platform_role === 'operator';

  const [sortKey, setSortKey] = useState<SortKey>('fqdn');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [addOpen, setAddOpen] = useState(false);
  const sorted = useMemo(() => {
    const list = domainsQ.data ?? [];
    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'nl');
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [domainsQ.data, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'fqdn' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Domeinen</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {domainsQ.data?.length ?? 0} gemonitorde domeinen
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-slate-900 hover:bg-brand-500"
          >
            <Plus size={12} />
            Domein toevoegen
          </button>
        )}
      </div>

      <QueryState
        isLoading={domainsQ.isLoading}
        isError={domainsQ.isError}
        onRetry={() => domainsQ.refetch()}
      >
        {sorted.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen domeinen toegevoegd"
              body="Voeg een domein toe om DMARC-rapportages te ontvangen. SerSo voorziet de DNS-record-instructies binnen één klik."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Domeinoverzicht" subtitle={`${sorted.length} domeinen`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <SortableTh
                      label="FQDN"
                      sortKey="fqdn"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Organizational
                    </th>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Beleid (gewenst / actueel)
                    </th>
                    <SortableTh
                      label="Sinds"
                      sortKey="monitored_since"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="DMARC 30d"
                      sortKey="pass_30d"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="SPF 30d"
                      sortKey="spf_pass_30d"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="DKIM 30d"
                      sortKey="dkim_pass_30d"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Alerts"
                      sortKey="alerts_count"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sorted.map((d) => (
                    <tr key={d.id} className="group cursor-pointer hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <Link
                          to="/tenants/$slug/domains/$id"
                          params={{ slug, id: d.id }}
                          className="flex items-center gap-2.5"
                        >
                          <StatusDot status={d.status ?? statusFromRates(d.pass_30d)} />
                          <span className="font-mono text-[13px] text-slate-900 group-hover:text-brand-700">
                            {d.fqdn}
                          </span>
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                        {d.organizational_domain ?? '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <PolicyPair desired={d.desired_policy} current={d.current_policy} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {d.monitored_since ? formatDate(d.monitored_since) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={pctOf(d.pass_30d)} width={52} />
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={pctOf(d.spf_pass_30d)} width={52} />
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={pctOf(d.dkim_pass_30d)} width={52} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {(d.alerts_count ?? 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-700">
                            <AlertCircle size={10} />
                            {d.alerts_count}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </QueryState>

      <p className="text-[11px] text-slate-400">
        Domein verwijderen werkt via de admin-console.
      </p>

      <DomainAddModal open={addOpen} onClose={() => setAddOpen(false)} slug={slug} />
    </div>
  );
}

function PolicyPair({
  desired,
  current,
}: {
  desired?: 'none' | 'quarantine' | 'reject';
  current?: 'none' | 'quarantine' | 'reject';
}) {
  return (
    <div className="flex items-center gap-1.5 font-mono text-[11px]">
      <PolicyBadge value={desired} label="gewenst" />
      <span className="text-slate-300">→</span>
      <PolicyBadge value={current} label="actueel" />
    </div>
  );
}

function PolicyBadge({
  value,
  label,
}: {
  value?: 'none' | 'quarantine' | 'reject';
  label: string;
}) {
  const map = {
    none: { text: 'text-slate-700', bg: 'bg-slate-100' },
    quarantine: { text: 'text-amber-700', bg: 'bg-amber-50' },
    reject: { text: 'text-green-700', bg: 'bg-green-50' },
  } as const;
  const v = value ?? 'none';
  const m = map[v];
  return (
    <span
      title={label}
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] ${m.bg} ${m.text}`}
    >
      p={v}
    </span>
  );
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sortKey === activeKey;
  return (
    <th
      className={`cursor-pointer select-none px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 text-${align}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-[9px]">{dir === 'desc' ? '↓' : '↑'}</span>}
      </span>
    </th>
  );
}
